import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import { MeetingRepository } from 'repository/meetingRepository';
import { SubPlaceModal, SubPlace, SubPlaceForm, getCategoryLabel, getCategoryIconEl } from 'components/meeting/SubPlaceModal';
import { shareMeeting, getMeetingShareUrl, copyToClipboard } from 'common/utils/share';
import { MapContainer } from 'components/map';
import { ExternalMapSheet } from 'components/map/ExternalMapSheet';
import {
    AppScreen, Badge, Button, ButtonFooter, CopyRow, InfoGrid, PageIntro,
    Section, SectionHeader,
} from 'components/ds';
import styles from './TravelMeetingShare.module.css';
import { unwrapList } from 'common/utils/apiResponse';

const STORAGE_KEY = 'meeting_create_data';

export const TravelMeetingShare = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const [copied, setCopied] = useState(false);
    const [linkCopied, setLinkCopied] = useState<null | 'ok' | 'fail'>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [meetingDetail, setMeetingDetail] = useState<any>(null);
    /* 집결지 미니지도를 눌렀을 때 뜨는 외부 지도앱 고르기 (카카오맵·네이버·티맵·구글) */
    const [showExternalMap, setShowExternalMap] = useState(false);

    // 서브 장소 모달 관련 상태
    const [showSubPlaceModal, setShowSubPlaceModal] = useState(false);
    const [editingSubPlace, setEditingSubPlace] = useState<SubPlace | null>(null);
    const [subPlaceForm, setSubPlaceForm] = useState<SubPlaceForm | undefined>(undefined);
    const [isCustomVisitTime, setIsCustomVisitTime] = useState(false);
    const [subPlaces, setSubPlaces] = useState<SubPlace[]>([]);
    const [locationStateProcessed, setLocationStateProcessed] = useState(false);

    // location.state에서 생성된 모임 데이터 가져오기
    // PlaceSearch에서 돌아올 때도 meetingId를 유지하기 위해 여러 소스에서 가져옴
    const createdMeeting = location.state?.meeting;
    const meetingId = createdMeeting?.meetingId || location.state?.meetingId || sessionStorage.getItem('travel_share_meeting_id');
    const inviteCode = createdMeeting?.inviteCode || location.state?.inviteCode;

    // meetingId를 sessionStorage에 저장 (PlaceSearch에서 돌아올 때 유지하기 위함)
    useEffect(() => {
        if (createdMeeting?.meetingId) {
            sessionStorage.setItem('travel_share_meeting_id', createdMeeting.meetingId);
        }
    }, [createdMeeting?.meetingId]);

    // 여행 기간 계산
    const travelPeriod = useMemo(() => {
        if (!meetingDetail?.travelStartDate || !meetingDetail?.travelEndDate) return null;
        const start = new Date(meetingDetail.travelStartDate);
        const end = new Date(meetingDetail.travelEndDate);
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        const nights = days - 1;
        return { days, nights, start, end };
    }, [meetingDetail?.travelStartDate, meetingDetail?.travelEndDate]);

    // 백엔드에서 모임 상세 조회
    const fetchMeetingDetail = useCallback(async () => {
        if (!meetingId) {
            setIsLoading(false);
            return;
        }

        try {
            const response = await MeetingRepository.onGetMeetingDetail(meetingId);
            console.log('모임 상세 조회 응답:', response);
            // API 응답 구조: response.data 또는 response.data.data
            const meetingInfo = response?.data?.data || response?.data;
            if (meetingInfo) {
                console.log('모임 정보:', meetingInfo);
                setMeetingDetail(meetingInfo);
            }
        } catch (error) {
            console.error('모임 상세 조회 실패:', error);
        } finally {
            setIsLoading(false);
        }
    }, [meetingId]);

    // 서브 장소 목록 조회
    const fetchSubPlaces = useCallback(async () => {
        if (!meetingId) return;
        try {
            setSubPlaces(unwrapList(await MeetingRepository.onGetSubPlaces(meetingId)));
        } catch (error) {
            console.error('서브 장소 목록 조회 실패:', error);
        }
    }, [meetingId]);

    // 초기 로드
    useEffect(() => {
        fetchMeetingDetail();
        fetchSubPlaces();
    }, [fetchMeetingDetail, fetchSubPlaces]);

    // PlaceSearch에서 돌아온 경우 처리 (한 번만 실행)
    useEffect(() => {
        if (location.state?.fromSubPlaceModal && !locationStateProcessed) {
            const savedForm = location.state.subPlaceForm;
            if (location.state?.placeInfo) {
                const placeInfo = location.state.placeInfo;
                setSubPlaceForm({
                    ...(savedForm || { placeName: '', placeAddress: '', category: 'ATTRACTION', dayNumber: 1, visitTime: '', description: '' }),
                    placeName: placeInfo.name || savedForm?.placeName || '',
                    placeAddress: placeInfo.address || savedForm?.placeAddress || ''
                });
            } else if (savedForm) {
                setSubPlaceForm(savedForm);
            }
            setIsCustomVisitTime(location.state.isCustomVisitTime || false);
            if (location.state.editingSubPlaceId) {
                const editingPlace = subPlaces.find(sp => sp.subPlaceId === location.state.editingSubPlaceId);
                if (editingPlace) {
                    setEditingSubPlace(editingPlace);
                }
            }
            setShowSubPlaceModal(true);
            setLocationStateProcessed(true);
        }
    }, [location.state, subPlaces, locationStateProcessed]);

    // 컴포넌트 언마운트 시 sessionStorage 정리
    useEffect(() => {
        return () => {
            sessionStorage.removeItem(STORAGE_KEY);
        };
    }, []);

    // 날짜 포맷팅 함수
    const formatDateRange = (startDate: string, endDate: string) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const weekdays = ['일', '월', '화', '수', '목', '금', '토'];

        const startStr = `${start.getMonth() + 1}/${start.getDate()}(${weekdays[start.getDay()]})`;
        const endStr = `${end.getMonth() + 1}/${end.getDate()}(${weekdays[end.getDay()]})`;

        return `${startStr} ~ ${endStr}`;
    };

    // sessionStorage에서 입력한 데이터 가져오기 (fallback용)
    const savedData = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');

    // 모임 데이터 (백엔드에서 가져온 데이터 우선, 없으면 sessionStorage fallback)
    const meetingData = useMemo(() => {
        // 백엔드 데이터가 있으면 우선 사용
        if (meetingDetail && Object.keys(meetingDetail).length > 0) {
            const dateRange = meetingDetail.travelStartDate && meetingDetail.travelEndDate
                ? formatDateRange(meetingDetail.travelStartDate, meetingDetail.travelEndDate)
                : '';

            // 숙소 정보 (API에서 직접 가져옴)
            const hotelName = meetingDetail.hotelName || meetingDetail.hotel_name || '-';

            return {
                name: meetingDetail.meetingName || '',
                destination: meetingDetail.travelDestination || '',
                destinationEmoji: meetingDetail.travelDestinationInfo?.emoji || '',
                dateRange,
                period: travelPeriod ? t('meetingFinished.nightsDays', { nights: travelPeriod.nights, days: travelPeriod.days }) : '',
                meetingTime: meetingDetail.meetingTime || '',
                meetingPlace: meetingDetail.placeName || '',
                /* 집결지 미니지도용 — 응답의 latitude/longitude 는 place_id 가 가리키는
                   집결지 place 에서 옵니다(MeetingDetailResponse.from). */
                meetingPlaceAddress: meetingDetail.placeAddress || '',
                lat: Number(meetingDetail.latitude) || null,
                lng: Number(meetingDetail.longitude) || null,
                hotelPlace: hotelName,
                inviteCode: meetingDetail.inviteCode || inviteCode || 'CREW-XXXXXX'
            };
        }

        // 백엔드 데이터가 없으면 sessionStorage fallback
        if (savedData && Object.keys(savedData).length > 0) {
            const dateRange = savedData.travelStartDate && savedData.travelEndDate
                ? formatDateRange(savedData.travelStartDate, savedData.travelEndDate)
                : '';

            const start = savedData.travelStartDate ? new Date(savedData.travelStartDate) : null;
            const end = savedData.travelEndDate ? new Date(savedData.travelEndDate) : null;
            let period = '';
            if (start && end) {
                const diffTime = Math.abs(end.getTime() - start.getTime());
                const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                const nights = days - 1;
                period = t('meetingFinished.nightsDays', { nights, days });
            }

            // 숙소 정보 (집결 장소와 동일한 경우 집결 장소 정보 사용)
            const hotelName = savedData.isSameAsMeetingPlace
                ? (savedData.meetingPlace?.name || '-')
                : (savedData.hotelPlace?.name || '-');

            return {
                name: savedData.meetingName || '',
                destination: savedData.destination || '',
                destinationEmoji: savedData.destinationInfo?.emoji || '',
                dateRange,
                period,
                meetingTime: savedData.meetingTime || '',
                meetingPlace: savedData.meetingPlace?.name || '',
                meetingPlaceAddress: savedData.meetingPlace?.address || '',
                lat: Number(savedData.meetingPlace?.lat) || null,
                lng: Number(savedData.meetingPlace?.lng) || null,
                hotelPlace: hotelName,
                inviteCode: inviteCode || 'CREW-XXXXXX'
            };
        }

        return null;
    }, [meetingDetail, travelPeriod, inviteCode, savedData]);

    const handleCopyCode = async () => {
        if (meetingData) {
            await copyToClipboard(meetingData.inviteCode);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleShare = async () => {
        if (!meetingData) return;
        await shareMeeting({
            meetingName: meetingData.name,
            date: meetingData.dateRange,
            location: meetingData.meetingPlace,
            inviteCode: meetingData.inviteCode,
            description: `${meetingData.destinationEmoji} ${meetingData.period}`,
        });
    };

    const handleShareLink = async () => {
        if (!meetingData) return;
        // showAlert 회귀 방지 — MeetingShare 와 동일 이유 (useBackDismiss history.back 경합).
        // 인라인 토스트로 피드백.
        const shareUrl = getMeetingShareUrl(meetingData.inviteCode);
        const success = await copyToClipboard(shareUrl);
        setLinkCopied(success ? 'ok' : 'fail');
        setTimeout(() => setLinkCopied(null), 2000);
    };

    // 서브 장소 추가 모달 열기
    const handleOpenSubPlaceModal = () => {
        setEditingSubPlace(null);
        setSubPlaceForm(undefined);
        setIsCustomVisitTime(false);
        setShowSubPlaceModal(true);
    };

    // 서브 장소 수정 모달 열기
    const handleEditSubPlace = (subPlace: SubPlace) => {
        setEditingSubPlace(subPlace);
        setSubPlaceForm(undefined); // SubPlaceModal will populate from editingSubPlace
        setIsCustomVisitTime(false);
        setShowSubPlaceModal(true);
    };

    // 서브 장소 모달 닫기
    const handleCloseSubPlaceModal = () => {
        setShowSubPlaceModal(false);
        setEditingSubPlace(null);
        setSubPlaceForm(undefined);
        setIsCustomVisitTime(false);
    };

    // 모바일 WebView 에서 react-router navigate 가 먹통인 회귀가 반복 → 네이티브 location 으로 강제.
    const handleComplete = () => {
        sessionStorage.removeItem('travel_share_meeting_id');
        try { sessionStorage.setItem('meeting_create_completed', '1'); } catch {}
        console.log('[TravelMeetingShare] complete clicked', meetingId);
        if (meetingId) {
            window.location.replace(`/meeting/detail/${meetingId}?att=1`);
        } else {
            window.location.replace('/home');
        }
    };

    const handleSkip = () => {
        sessionStorage.removeItem('travel_share_meeting_id');
        try { sessionStorage.setItem('meeting_create_completed', '1'); } catch {}
        console.log('[TravelMeetingShare] skip clicked', meetingId);
        if (meetingId) {
            window.location.replace(`/meeting/detail/${meetingId}?att=1`);
        } else {
            window.location.replace('/home');
        }
    };

    if (isLoading) {
        return (
            <AppScreen title={t('travelShare.headerTitle')} bar="close" onClose={handleComplete}>
                <p className={styles.state}>{t('travelShare.loadingInfo')}</p>
            </AppScreen>
        );
    }

    if (!meetingData) {
        return (
            <AppScreen
                title={t('travelShare.headerTitle')}
                bar="close"
                onClose={handleComplete}
            >
                <p className={styles.state}>{t('travelShare.loadFail')}</p>
                <Section spacing="tight">
                    <Button variant="primary" size="lg" block onClick={() => navigate('/home', { replace: true })}>
                        {t('meetingShare.goHome')}
                    </Button>
                </Section>
            </AppScreen>
        );
    }

    return (
        <AppScreen
            title={t('travelShare.headerTitle')}
            bar="close"
            hasFooter
            onClose={handleComplete}
        >
            <PageIntro
                title={t('travelShare.readyTitle', { destination: meetingData.destination })}
                desc={meetingData.name}
            />

            {/* 여행 요약 */}
            <Section spacing="tight">
                <div className={styles.periodBadge}>
                    <Badge tone="lime">{meetingData.period}</Badge>
                </div>
                <InfoGrid
                    items={[
                        { label: t('travelShare.tripPeriod'), value: meetingData.dateRange, full: true },
                        { label: t('travelShare.gatherTime'), value: meetingData.meetingTime },
                        { label: t('meetingShare.gatherPlace'), value: meetingData.meetingPlace },
                        { label: t('meetingShare.hotel'), value: meetingData.hotelPlace, full: true },
                    ]}
                />

                {/* 집결지 미니지도 — 아직 출발 전이라 "어디로 모이나" 가 가장 급한 정보입니다.
                    누르면 카카오맵·네이버지도·티맵으로 바로 길찾기할 수 있게 시트를 엽니다.
                    집결지를 아직 안 정했으면 지도 대신 정하러 가는 길을 둡니다. */}
                {meetingData.lat && meetingData.lng ? (
                    <div
                        className={styles.gatherMap}
                        role="button"
                        tabIndex={0}
                        onClick={() => setShowExternalMap(true)}
                        onKeyDown={(e) => { if (e.key === 'Enter') setShowExternalMap(true); }}
                    >
                        <MapContainer
                            center={{ lat: meetingData.lat, lng: meetingData.lng }}
                            markers={[{
                                id: 'gather',
                                lat: meetingData.lat,
                                lng: meetingData.lng,
                                label: '',
                                tooltip: meetingData.meetingPlace,
                                isDestination: true,
                            }]}
                            height={160}
                            zoomLevel={5}
                            placeName={meetingData.meetingPlace}
                        />
                    </div>
                ) : meetingId ? (
                    <Button
                        variant="secondary"
                        block
                        onClick={() => navigate(`/meeting/edit/${meetingId}`, { state: { openGather: true } })}
                    >
                        {t('meeting.detailV6.gatherMissing')}
                    </Button>
                ) : null}
            </Section>

            {/* 초대 */}
            <Section divided>
                <SectionHeader title={t('meetingShare.inviteCode')} />
                <CopyRow
                    label={t('meetingShare.inviteCode')}
                    value={meetingData.inviteCode}
                    copyLabel={copied ? t('meetingShare.copied') : t('meetingShare.copy')}
                    onCopy={handleCopyCode}
                />
                <div className={styles.shareActions}>
                    <Button variant="secondary" size="lg" block onClick={handleShare}>
                        {t('meetingShare.shareBtn')}
                    </Button>
                    <Button variant="ghost" size="lg" block onClick={handleShareLink}>
                        {linkCopied === 'ok' ? t('meetingShare.linkCopied') : t('meetingShare.copyLink')}
                    </Button>
                </div>
            </Section>

            {/* 갈 곳 미리 담기 */}
            <Section divided last>
                <SectionHeader
                    title={t('travelShare.addPlaces')}
                    sub={t('travelShare.addPlacesSub')}
                    count={subPlaces.length || undefined}
                />

                {subPlaces.length > 0 && (
                    <ul className={styles.list}>
                        {subPlaces.map((place) => (
                            <li key={place.subPlaceId}>
                                <button
                                    type="button"
                                    className={styles.row}
                                    onClick={() => handleEditSubPlace(place)}
                                >
                                    <span className={styles.rowIcon}>
                                        {getCategoryIconEl(place.category, 16)}
                                    </span>
                                    <span className={styles.rowBody}>
                                        <span className={styles.rowTitle}>{place.placeName}</span>
                                        <span className={styles.rowMeta}>
                                            {getCategoryLabel(place.category)}
                                            {place.dayNumber ? ` · DAY ${place.dayNumber}` : ''}
                                        </span>
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}

                <div className={styles.addAction}>
                    <Button variant="secondary" size="lg" block onClick={handleOpenSubPlaceModal}>
                        {t('travelShare.addPlaceBtn')}
                    </Button>
                </div>
                <p className={styles.hint}>{t('travelShare.addPlaceHint')}</p>
            </Section>

            <ButtonFooter>
                <Button variant="ghost" size="lg" onClick={handleSkip}>
                    {t('travelShare.skip')}
                </Button>
                <Button variant="primary" size="lg" block onClick={handleComplete}>
                    {t('travelShare.viewMeeting')}
                </Button>
            </ButtonFooter>

            <SubPlaceModal
                isOpen={showSubPlaceModal}
                onClose={handleCloseSubPlaceModal}
                meetingId={meetingId}
                editingSubPlace={editingSubPlace}
                isTravelMeeting
                travelPeriod={travelPeriod}
                meetingTime={meetingDetail?.meetingTime}
                subPlaces={subPlaces}
                onSaveSuccess={fetchSubPlaces}
                returnTo="/meeting/travel-share"
                initialForm={subPlaceForm}
                initialIsCustomVisitTime={isCustomVisitTime}
            />

            {/* 외부 지도앱 — 국내는 카카오맵·네이버지도·티맵, 해외는 구글맵.
                시트 자체는 이미 있던 공용 컴포넌트를 그대로 씁니다. */}
            <ExternalMapSheet
                visible={showExternalMap}
                placeName={meetingData.meetingPlace}
                address={meetingData.meetingPlaceAddress || ''}
                lat={meetingData.lat || 0}
                lng={meetingData.lng || 0}
                onClose={() => setShowExternalMap(false)}
            />
        </AppScreen>
    );
};
